import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, type CoursePeople } from '../../api';

export function AdminCourse() {
  const { id } = useParams();
  const [data, setData] = useState<CoursePeople | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setError(null);
    const q = new URLSearchParams({ pageSize: '200' });
    if (filter) q.set('status', filter);
    try { setData(await api.get<CoursePeople>(`/admin/courses/${id}/people?${q}`)); }
    catch (e) { setError((e as ApiError).message); }
  }, [id, filter]);

  useEffect(() => { void load(); }, [load]);

  if (error) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const done = data.people.filter((p) => p.status === 'completed').length;

  return (
    <>
      <nav className="crumbs"><Link to="/admin/courses">Courses</Link> <span aria-hidden>›</span> {data.course.name}</nav>
      <div className="page-head">
        <h1 className="h1">{data.course.name}</h1>
        <Link className="btn btn--ghost" to={`/admin/courses/${data.course.id}/edit`}>Edit content</Link>
      </div>

      <dl className="facts facts--wide">
        <div><dt>On the course</dt><dd>{data.total}</dd></div>
        <div><dt>Completed</dt><dd>{done}</dd></div>
        <div><dt>Completion rate</dt><dd>{data.total ? Math.round((done / data.total) * 100) : 0}%</dd></div>
      </dl>

      <div className="filters">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Progress">
          <option value="">Everyone</option>
          <option value="completed">Completed</option>
          <option value="incomplete">In progress</option>
          <option value="not_attempted">Not started</option>
          <option value="failed">Not passed</option>
        </select>
      </div>

      <table className="grid">
        <thead><tr><th>Name</th><th>Role</th><th>Progress</th><th>Status</th><th>Last opened</th></tr></thead>
        <tbody>
          {data.people.length === 0 && <tr><td colSpan={5} className="muted">Nobody here.</td></tr>}
          {data.people.map((p) => (
            <tr key={p.userId} className={p.accountStatus === 'inactive' ? 'is-off' : ''}>
              <td>
                <Link to={`/admin/people/${p.userId}`}>{p.firstName} {p.lastName}</Link>
                {p.accountStatus === 'inactive' && <span className="tag">deactivated</span>}
              </td>
              <td className="muted">{p.role}</td>
              <td>
                <div className="bar bar--sm"><span style={{ width: `${p.completionPct}%` }} /></div>
                <span className="muted small">{p.completionPct}%{p.score != null ? ` · ${p.score}%` : ''}</span>
              </td>
              <td><span className={`pill pill--${p.status}`}>{p.status.replace('_', ' ')}</span></td>
              <td className="muted small">{p.lastAccessedAt ? new Date(p.lastAccessedAt).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
